import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import AdminCandidateCard from "../../components/molecules/AdminCandidateCard";
import AdminCandidateModal from "../../components/molecules/AdminCandidateModal";
import SectionBox from "../../components/layout/SectionBox";
import AdminHeader from "../../components/organisms/AdminHeader";
import Footer from "../../components/organisms/Footer";
import { useCandidateStore } from "../../store/useCandidateStore";

export default function AdminManageCandidatesPage() {
    const navigate = useNavigate();
    const { id: vacancyId } = useParams<{ id: string }>();

    const [activeTab, setActiveTab] = useState<"applied" | "reviewed" | "interviewed" | "hired" | "rejected">("applied");
    const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);


    const {
        sidebarInfo,
        candidates,
        pagination,
        fetchSidebarInfo,
        fetchCandidatesByStatus,
        loading,
    } = useCandidateStore();


    const openDetailModal = (candidate: any) => {
        setSelectedCandidate(candidate);
        setShowModal(true);
    };

    // ====================
    // Fetch sidebar & candidates on mount
    // ====================
    useEffect(() => {
        if (!vacancyId) return;

        fetchSidebarInfo(vacancyId);
    }, [vacancyId]);

    useEffect(() => {
        if (!vacancyId) return;

        fetchCandidatesByStatus(vacancyId, activeTab, currentPage);
    }, [vacancyId, activeTab, currentPage]);


    // ====================
    // Tabs mapping
    // ====================
    const statusTabs: Record<typeof activeTab, { label: string; count: number }> = {
        applied: { label: "Applied", count: sidebarInfo?.status_counts.find(s => s.status === "applied")?.total || 0 },
        reviewed: { label: "Reviewed", count: sidebarInfo?.status_counts.find(s => s.status === "reviewed")?.total || 0 },
        interviewed: { label: "Interviewed", count: sidebarInfo?.status_counts.find(s => s.status === "interviewed")?.total || 0 },
        hired: { label: "Hired", count: sidebarInfo?.status_counts.find(s => s.status === "hired")?.total || 0 },
        rejected: { label: "Rejected", count: sidebarInfo?.status_counts.find(s => s.status === "rejected")?.total || 0 },
    };

    return (
        <>
            <AdminHeader />

            <main>
                <div className="container-section mx-auto px-6 pt-6 pb-6">
                    <SectionBox className="mt-24">
                        <div>
                            {/* Back */}
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center gap-2 text-white font-medium"
                            >
                                <ChevronLeft size={18} /> Back to Job Dashboard
                            </button>

                            <div className="flex flex-col lg:flex-row gap-6 mt-6">
                                {/* ===== LEFT SIDEBAR ===== */}
                                <div className="w-full lg:w-72 bg-white border rounded-xl p-4 h-fit shadow-sm">
                                    {/* Job Info */}
                                    <div className="mb-6">
                                        <h2 className="text-lg font-semibold">{sidebarInfo?.vacancy_name}</h2>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Created on{" "}
                                            <span className="font-medium text-gray-800">
                                                {sidebarInfo?.created_at ? new Date(sidebarInfo.created_at).toLocaleDateString() : "-"}
                                            </span>
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            Closing In{" "}
                                            <span className="font-medium text-gray-800">
                                                {sidebarInfo?.expired_at ? new Date(sidebarInfo.expired_at).toLocaleDateString() : "-"}
                                            </span>
                                        </p>
                                    </div>
                                    <h2 className="text-lg flex">Total Applicant:
                                        <h2 className="font-semibold text-lg ml-2">
                                            {sidebarInfo?.total_applications}</h2>
                                    </h2>

                                    {/* Stage Tabs */}
                                    <h2 className="text-lg font-semibold">Candidate Stages</h2>
                                    <div className="mt-4 space-y-3 text-gray-700">
                                        {Object.entries(statusTabs).map(([status, info]) => (
                                            <button
                                                key={status}
                                                onClick={() => {
                                                    setActiveTab(status as typeof activeTab)
                                                    setCurrentPage(1);
                                                }}
                                                className={`flex justify-between w-full py-2 px-3 rounded-lg 
                                                    ${activeTab === status ? "bg-primary text-white" : "hover:bg-gray-100"}`}
                                            >
                                                <span>{info.label}</span>
                                                <span>{info.count}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* ===== RIGHT CONTENT ===== */}
                                <div className="flex-1 bg-white border rounded-xl p-4 sm:p-6 shadow-sm">
                                    <h2 className="text-xl font-semibold mb-4">
                                        {statusTabs[activeTab].label} Candidates
                                    </h2>

                                    {loading ? (
                                        <p className="text-gray-500 text-center mt-20">Loading...</p>
                                    ) : candidates[activeTab].length === 0 ? (
                                        <p className="text-gray-500 text-center mt-20">
                                            No candidates in this stage.
                                        </p>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {candidates[activeTab].map((c, i) => (
                                                <AdminCandidateCard
                                                    key={i}
                                                    candidate={c}
                                                    onViewDetail={() => openDetailModal(c)}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {pagination?.[activeTab] && (
                                            <div className="flex justify-center items-center mt-6 gap-4">
                                                <button
                                                    disabled={pagination[activeTab]!.current_page === 1}
                                                    onClick={() => setCurrentPage((prev) => prev - 1)}
                                                    className="px-4 py-2 border rounded disabled:opacity-50"
                                                >
                                                    Prev
                                                </button>

                                                <span className="text-sm">
                                                    Page {pagination[activeTab]!.current_page} of{" "}
                                                    {pagination[activeTab]!.last_page}
                                                </span>

                                                <button
                                                    disabled={
                                                        pagination[activeTab]!.current_page ===
                                                        pagination[activeTab]!.last_page
                                                    }
                                                    onClick={() => setCurrentPage((prev) => prev + 1)}
                                                    className="px-4 py-2 border rounded disabled:opacity-50"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        )}


                                    {showModal && selectedCandidate && (
                                        <AdminCandidateModal
                                            data={selectedCandidate}
                                            onClose={() => setShowModal(false)}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </SectionBox>
                </div>
            </main>

            <Footer />
        </>
    );
}
